import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from 'src/app/models/apiResponse.model';
import { ApiFilter, FieldsSelection, OrderByConfig, GroupByConfig, DefaultFilter } from 'src/app/models/apiParams.model';
import { addGaspedaalAlias } from 'src/app/models/request.model';
import {
    WaardebepalingCreateValuationResponse,
    WaardebepalingFeedbackRequest,
    WaardebepalingFeedbackResponse,
    WaardebepalingValuation,
} from 'src/app/models/waardebepaling.model';
import { BaseApiService } from '../base/base-api.service';

/**
 * Third Party Service - Handles 3rd-party integrations (e.g. Gaspedaal make/model data and aliases, ATP Waardebepaling)
 */
@Injectable({ providedIn: 'root' })
export class ThirdPartyService extends BaseApiService {
    private readonly gaspedaalPath = '/3rd-party/v1/gaspedaal';
    private readonly gaspedaalMakePath = '/3rd-party/v1/gaspedaal/make/';
    private readonly gaspedaalAliasPath = '/3rd-party/v1/gaspedaal/translation';
    private readonly valuationPath = '/request/v1/valuation';

    constructor(http: HttpClient) {
        super(http);
    }

    /*-------------------------
    == GASPEDAAL CALLS ==
    -------------------------*/

    getGaspedaalMakes(limit: number, page: number, orderBy?: string | OrderByConfig[], filter?: string | ApiFilter, fields?: FieldsSelection, defaultFilter?: DefaultFilter, groupBy?: GroupByConfig): Observable<ApiResponse> {
        return this.getAll<ApiResponse>(this.gaspedaalPath + '/make', { limit, page, orderBy, filter, fields, defaultFilter, groupBy });
    }

    getGaspedaalMake(id: number | string, fields?: FieldsSelection): Observable<ApiResponse> {
        return this.getById<ApiResponse>(this.gaspedaalMakePath, id, { fields });
    }

    addGaspedaalAlias(data: addGaspedaalAlias, customRequestId?: string): Observable<ApiResponse> {
        return this.postWithCustomId<ApiResponse>(this.gaspedaalAliasPath, data, customRequestId, 'json');
    }

    deleteGaspedaalAlias(id: number | string): Observable<ApiResponse> {
        return this.delete<ApiResponse>(this.gaspedaalAliasPath + '/', id);
    }

    getGaspedaalOptions(): Observable<ApiResponse> {
        return this.options<ApiResponse>(this.gaspedaalPath);
    }

    /*-------------------------
    == ATP WAARDEBEPALING CALLS ==
    All calls take the ATP valuation id (not the Waardebepaling uuid). On an upstream
    error `data` is a `WaardebepalingUpstreamError` (e.g. 429 with `retry_after`), otherwise null.
    -------------------------*/

    /** Request a price indication via ATP Waardebepaling (processed asynchronously; poll with `getWaardebepaling`). */
    submitWaardebepaling(valuationId: number): Observable<ApiResponse<WaardebepalingCreateValuationResponse>> {
        return this.post<ApiResponse<WaardebepalingCreateValuationResponse>>(`${this.valuationPath}/${valuationId}/price-indication`, null, 'json');
    }

    /** Status and result; atp-api stores the result on the specification once the status is terminal. */
    getWaardebepaling(valuationId: number): Observable<ApiResponse<WaardebepalingValuation>> {
        return this.getAll<ApiResponse<WaardebepalingValuation>>(`${this.valuationPath}/${valuationId}/price-indication`);
    }

    postWaardebepalingFeedback(valuationId: number, data: WaardebepalingFeedbackRequest): Observable<ApiResponse<WaardebepalingFeedbackResponse>> {
        return this.post<ApiResponse<WaardebepalingFeedbackResponse>>(`${this.valuationPath}/${valuationId}/price-indication-feedback`, data, 'json');
    }
}
